import type { Class, ProjectKind, Sample } from '../data/types';

/**
 * Gentle observations about a dataset — phrased as invitations, not errors.
 *
 * Per CLAUDE.md: "Dataset balance and capture diversity are surfaced as
 * observations, not errors." The product never blocks training on these;
 * it just nudges the kid toward a richer dataset when one would help.
 */

export type ObservationKind = 'imbalance' | 'thin-class' | 'narrow-window' | 'face-bias';

export interface DatasetObservation {
  kind: ObservationKind;
  /** Kid-facing copy, already filled in with class names. */
  message: string;
  /** The class this observation is about (or undefined for project-wide). */
  classId?: string;
}

/** A class is "thin" when it has barely more than the training minimum. */
const THIN_CLASS_THRESHOLD = 6;
/** A class is imbalanced when one has > IMBALANCE_RATIO × another. */
const IMBALANCE_RATIO = 3;
/** A class is "narrow" when all of its samples were captured within this
 * span. Hint to the kid that the model has only seen one moment in time. */
const NARROW_WINDOW_MS = 10_000;
/** Don't bother with the narrow-window hint until there are at least this
 * many samples — a one-shot capture is fine, holding-to-record is the case
 * we're nudging away from. */
const NARROW_WINDOW_MIN_SAMPLES = 8;

export function observeDataset(
  classes: Pick<Class, 'id' | 'name'>[],
  samples: Pick<Sample, 'classId' | 'createdAt'>[],
  kind: ProjectKind = 'image',
): DatasetObservation[] {
  if (classes.length < 2) return [];
  const noun = kind === 'audio' ? 'clips' : 'pictures';
  const burstCopy =
    kind === 'audio'
      ? 'Try grabbing a few more from a different angle or in a quieter moment.'
      : 'Try grabbing a few more from a different angle.';

  const counts = new Map<string, number>();
  const earliestByClass = new Map<string, number>();
  const latestByClass = new Map<string, number>();
  for (const sample of samples) {
    counts.set(sample.classId, (counts.get(sample.classId) ?? 0) + 1);
    const earliest = earliestByClass.get(sample.classId);
    if (earliest === undefined || sample.createdAt < earliest) {
      earliestByClass.set(sample.classId, sample.createdAt);
    }
    const latest = latestByClass.get(sample.classId);
    if (latest === undefined || sample.createdAt > latest) {
      latestByClass.set(sample.classId, sample.createdAt);
    }
  }

  const observations: DatasetObservation[] = [];

  // Imbalance: surface only the worst pair so the UI doesn't get noisy.
  let mostId: string | null = null;
  let mostCount = -1;
  let leastId: string | null = null;
  let leastCount = Number.POSITIVE_INFINITY;
  for (const cls of classes) {
    const count = counts.get(cls.id) ?? 0;
    if (count > mostCount) {
      mostCount = count;
      mostId = cls.id;
    }
    if (count < leastCount) {
      leastCount = count;
      leastId = cls.id;
    }
  }
  if (
    mostId &&
    leastId &&
    mostId !== leastId &&
    leastCount > 0 &&
    mostCount >= leastCount * IMBALANCE_RATIO
  ) {
    const mostName = classes.find((c) => c.id === mostId)?.name ?? mostId;
    const leastName = classes.find((c) => c.id === leastId)?.name ?? leastId;
    observations.push({
      kind: 'imbalance',
      message: `${mostName} has lots more ${noun} than ${leastName}. Want to add a few more for ${leastName}?`,
      classId: leastId,
    });
  }

  // Thin classes: at or just above the minimum. We only flag classes that
  // are within 1 of the minimum to keep this from firing for a healthy mid-
  // sized dataset.
  for (const cls of classes) {
    const count = counts.get(cls.id) ?? 0;
    if (count > 0 && count < THIN_CLASS_THRESHOLD) {
      observations.push({
        kind: 'thin-class',
        message: `${cls.name} only has ${count} ${noun} so far. A bit more variety helps the model learn.`,
        classId: cls.id,
      });
    }
  }

  // Narrow capture window: all samples in one quick burst.
  for (const cls of classes) {
    const count = counts.get(cls.id) ?? 0;
    if (count < NARROW_WINDOW_MIN_SAMPLES) continue;
    const earliest = earliestByClass.get(cls.id);
    const latest = latestByClass.get(cls.id);
    if (earliest === undefined || latest === undefined) continue;
    if (latest - earliest < NARROW_WINDOW_MS) {
      observations.push({
        kind: 'narrow-window',
        message: `All of ${cls.name}'s ${noun} were captured in one quick burst. ${burstCopy}`,
        classId: cls.id,
      });
    }
  }

  return observations;
}
